import { useEffect, useRef, useState, type ReactNode } from 'react';

import { AppLink } from '../link.js';
import { useLocation } from '../router.js';
import { RECORD_PAGES, recordHref, sectionHref, type SectionKey } from './project-sections.js';

/**
 * The links between registers, and the highlight at the other end.
 *
 * WHY
 * ---
 * The user's second ask: *"connect the sections to each other"*. A material is
 * blocked by a submittal, that submittal was raised against a drawing, an NCR
 * came out of an inspection — every one of those relationships was already IN
 * the data and printed as a bare number on the row. `SUB-0007` told you what to
 * search for; it did not take you there.
 *
 * HOW
 * ---
 * A cross-link is `/projects/<id>/<section>?focus=<record>`. The target section
 * renders normally; the row whose id matches `focus` marks itself and scrolls
 * into view. Nothing new is fetched and no register learns about another one's
 * internals — the link carries an id and the destination recognises it.
 *
 * WHAT THESE LINKS NEVER DO
 * -------------------------
 * Claim a relationship the data does not hold. Every link here is drawn from a
 * foreign key the record already carries, or from a list the screen already
 * has. Where the model has no link — an NCR and a snag are not related rows —
 * the words say what was actually matched on, and there is no link pretending
 * otherwise.
 */

/** The `?focus=` id, if the current URL carries one. */
export function useFocusedId(): string | null {
  const { search } = useLocation();
  return new URLSearchParams(search).get('focus');
}

/**
 * Is this row the one that was linked to? And a ref to scroll it into view.
 *
 * The scroll happens once, when the row first appears as the focused one — not
 * on every render, or a register that reloads after a write would yank the page
 * back to a row somebody had already scrolled away from.
 */
export function useFocusRow<T extends HTMLElement>(id: string): {
  readonly focused: boolean;
  readonly ref: React.RefObject<T>;
} {
  const focusedId = useFocusedId();
  const focused = focusedId !== null && focusedId === id;
  const ref = useRef<T>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!focused || scrolled) return;
    const element = ref.current;
    if (!element) return;
    setScrolled(true);
    // jsdom has no layout and no `scrollIntoView`, and a real browser five
    // years from now may have dropped the options object. Neither is worth an
    // exception on a decoration.
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'center' });
    }
  }, [focused, scrolled]);

  return { focused, ref };
}

/** `is-focused` on the row the link pointed at, for the caller's className. */
export function focusClass(focused: boolean, base = ''): string {
  return focused ? `${base} is-focused`.trim() : base;
}

/**
 * A link from one register to a record in another.
 *
 * The visible text is the record's own reference — `SUB-0007`, `DWG-014` — and
 * the hidden text says what the relationship IS, because "SUB-0007" read out on
 * its own in a table of materials is a link to nowhere describable.
 *
 * WHERE IT LANDS, AND WHY THAT CHANGED (§4.3)
 * -------------------------------------------
 * A cross-link's whole job is "the record I mean", and `?focus=` was the best
 * this product could do while records had no address of their own: navigate to
 * the register and mark one row. Records have addresses now, and a link straight
 * to one is strictly better — it opens the thing rather than the haystack around
 * it, and it cannot be defeated by a filter on the destination register that
 * hides the very row it came to mark. (Snags opens on the unfinished ones. A
 * `?focus=` at a closed snag has always landed on nothing.)
 *
 * So a caller naming a `register` that HAS a page gets the page; every other
 * caller gets `?focus=` exactly as before. That is what "make `?focus=`
 * redundant rather than sit beside it" means in practice — it retires one call
 * site at a time as each register gains a page, instead of on a flag day.
 */
export function CrossLink({
  projectId,
  section,
  register,
  focus,
  children,
  says,
}: {
  readonly projectId: string;
  readonly section: SectionKey;
  /**
   * The register the record lives in, when it has a page of its own.
   *
   * Named rather than derived from `section`, which cannot say it: `design`
   * holds both drawings and submittals. A register absent from `RECORD_PAGES`
   * falls back to `?focus=` — that absence is a decision, not a gap, and
   * `project-sections.ts` says which and why.
   */
  readonly register?: string;
  /** The record to land on. Omitted for a link to the register itself. */
  readonly focus?: string | null;
  readonly children: ReactNode;
  /** The relationship, for a screen reader: "the submittal blocking this". */
  readonly says: string;
}): JSX.Element {
  const to =
    register && focus && RECORD_PAGES[register]
      ? recordHref(projectId, register, focus)
      : sectionHref(projectId, section, focus ?? undefined);

  return (
    <AppLink className="cross-link" to={to}>
      {children}
      <span className="visually-hidden"> — {says}</span>
    </AppLink>
  );
}

// ---------------------------------------------------------------------------
// Arriving with an intent: `?new=`
// ---------------------------------------------------------------------------

/**
 * `/projects/<id>/<section>?new=<register>` — arrive with the form already open.
 *
 * The sibling of `?focus=`, and it works the same way: the link carries a word,
 * the destination recognises it, and nothing new is fetched. `?focus=` says
 * "the record I mean"; this says "the thing I came to write".
 *
 * It exists for the site home (`SiteHome.tsx`). Somebody standing on a floor
 * holding a phone should not tap a tile, wait for a register to load, and then
 * hunt for "Raise a snag" — that is two taps and a read where one tap will do.
 *
 * ONCE PER ARRIVAL, and that is what the ref is for. The effect re-runs when
 * the register reloads after the write; without the guard, saving a snag would
 * immediately reopen the form to write another one.
 *
 * NOT EVERY TILE CAN USE IT. Recording progress and raising a blocker both act
 * on a record somebody has to choose first, so their tiles land on the list —
 * which is the flow, not a shortfall. A `?new=` that opened a form with no
 * subject would be a form that cannot be submitted.
 */
export function useArrivedToCreate(register: string, open: () => void): void {
  const { search } = useLocation();
  const wanted = new URLSearchParams(search).get('new');
  const done = useRef(false);

  useEffect(() => {
    if (done.current || wanted !== register) return;
    done.current = true;
    open();
  }, [wanted, register, open]);
}
